/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React from 'react';
import {
  ActivityIndicator,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  useWindowDimensions,
  View,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { spacing } from '../../shared/theme/spacing'
import { typography } from '../../shared/theme/typography';
import { ticketColors } from '../../shared/theme/ticketColors';
import { settingsStyles } from '../../settings/screens/settingsScreen.styles';
import { StatusPill } from '../../shared/surface-cards/StatusPill';
import type { BranchFiscalSummary } from '../domain/fiscalTypes';
import { NumericTextInput } from '../../shared/ui/NumericTextInput';

export type BranchFiscalRowState = {
  branchId: string;
  branchName: string;
  pointOfSale: string;
  pvValidatedAt: string | null;
  pvLastInvoiceNumber: number | null;
  pvSetupError: string | null;
  validating?: boolean;
};

export type BranchFiscalSettingsPanelProps = {
  rows: BranchFiscalRowState[];
  currentBranchId: string | null;
  canEdit: boolean;
  disabled?: boolean;
  credentialsValidated?: boolean;
  onPointOfSaleChange: (branchId: string, value: string) => void;
  onValidateBranch: (branchId: string) => void;
  onValidateAll?: () => void;
  validatingAll?: boolean;
};

function pvStatus(row: BranchFiscalRowState): 'ok' | 'missing' | 'error' | 'pending' {
  if (row.pvSetupError) return 'error';
  if (row.pvValidatedAt) return 'ok';
  const pv = Number(row.pointOfSale);
  if (!Number.isFinite(pv) || pv <= 0) return 'missing';
  return 'pending';
}

export function BranchFiscalSettingsPanel({
  rows,
  currentBranchId,
  canEdit,
  disabled = false,
  credentialsValidated = false,
  onPointOfSaleChange,
  onValidateBranch,
  onValidateAll,
  validatingAll = false,
}: BranchFiscalSettingsPanelProps) {
  const { width } = useWindowDimensions();
  const wide = width >= 768;
  const verifyAllDisabled = disabled || validatingAll || !canEdit || !credentialsValidated;

  return (
    <View style={styles.shell}>
      <Text style={styles.intro}>
        En ARCA están en «Puntos de Venta», tipo Web Service — si no tenés uno, creálo ahí. Cada local puede usar el suyo o compartir el mismo número. Si un local no emite facturas, podés dejarlo vacío.
      </Text>
      {!credentialsValidated ? (
        <Text style={styles.intro}>
          Conectá ARCA primero. Después podés verificar cada local.
        </Text>
      ) : null}

      {onValidateAll ? (
        <TouchableOpacity
          style={[settingsStyles.secondaryButton, wide && styles.validateAllWide, verifyAllDisabled && styles.disabled]}
          onPress={onValidateAll}
          disabled={verifyAllDisabled}
          accessibilityRole="button"
          accessibilityHint={!credentialsValidated ? 'Conectá ARCA primero.' : undefined}
          accessibilityState={{ disabled: verifyAllDisabled }}
          testID="fiscal.branch-verify-all"
        >
          {validatingAll ? (
            <ActivityIndicator color={ticketColors.gold} />
          ) : (
            <Text style={settingsStyles.secondaryButtonText}>Verificar todos en ARCA</Text>
          )}
        </TouchableOpacity>
      ) : null}

      <View style={styles.stack}>
        {rows.map((row) => {
          const status = pvStatus(row);
          const isCurrent = row.branchId === currentBranchId;
          const verifyDisabled = row.validating || disabled || !canEdit || !credentialsValidated;
          const pill =
            status === 'ok' ? (
              <StatusPill label="PV válido en ARCA" tone="success" iconName="checkmark-circle" compact />
            ) : status === 'error' ? (
              <StatusPill label="Error ARCA" tone="danger" iconName="alert-circle-outline" compact />
            ) : status === 'missing' ? (
              <StatusPill label="Sin punto de venta" tone="warning" iconName="remove-circle-outline" compact />
            ) : (
              <StatusPill label="Sin verificar" tone="warning" iconName="time-outline" compact />
            );
          return (
            <View key={row.branchId} style={[styles.branchRow, isCurrent && styles.currentRow, wide && styles.branchRowWide]}>
              <View style={wide ? styles.rowInfoWide : styles.rowInfo}>
                <View style={styles.wideRowHeader}>
                  <Text style={styles.branchName} numberOfLines={wide ? 1 : undefined}>
                    {row.branchName}
                    {isCurrent ? ' · actual' : ''}
                  </Text>
                  {pill}
                </View>
                {row.pvLastInvoiceNumber != null ? (
                  <Text style={styles.meta}>
                    {`Último comprobante ARCA: ${row.pvLastInvoiceNumber}`}
                  </Text>
                ) : null}
                {row.pvSetupError ? <Text style={styles.errorText}>{row.pvSetupError}</Text> : null}
              </View>
              <View style={wide ? styles.pvFieldWide : undefined}>
                <Text style={styles.label}>Punto de venta</Text>
                <NumericTextInput
                  kind="integer"
                  accessibilityLabel={`Punto de venta de ${row.branchName}`}
                  testID={`fiscal.branch-pv.${row.branchId}`}
                  style={[styles.input, wide && styles.inputWide]}
                  value={row.pointOfSale}
                  onChangeText={(v) => onPointOfSaleChange(row.branchId, v)}
                  placeholder="1"
                  placeholderTextColor={ticketColors.ink2}
                  editable={canEdit && !disabled}
                />
              </View>
              <TouchableOpacity
                style={[
                  wide ? styles.validateBtn : settingsStyles.secondaryButton,
                  wide && styles.validateBtnWide,
                  verifyDisabled && styles.disabled,
                ]}
                onPress={() => onValidateBranch(row.branchId)}
                disabled={verifyDisabled}
                accessibilityRole="button"
                accessibilityLabel={`Verificar el punto de venta de ${row.branchName} en ARCA`}
                accessibilityHint={!credentialsValidated ? 'Conectá ARCA primero.' : undefined}
                accessibilityState={{ disabled: verifyDisabled }}
                testID={`fiscal.branch-verify.${row.branchId}`}
              >
                {row.validating ? (
                  <ActivityIndicator color={ticketColors.gold} size="small" />
                ) : (
                  <>
                    {wide ? <Ionicons name="shield-checkmark-outline" size={16} color={ticketColors.ink} /> : null}
                    <Text style={wide ? styles.validateBtnText : settingsStyles.secondaryButtonText}>
                      Verificar en ARCA
                    </Text>
                  </>
                )}
              </TouchableOpacity>
            </View>
          );
        })}
      </View>
    </View>
  );
}

export function branchFiscalRowsFromSummaries(
  summaries: BranchFiscalSummary[] | undefined,
  branchPvMap: Record<string, string>,
  branches: { id: string; name: string }[]
): BranchFiscalRowState[] {
  const byId = new Map((summaries ?? []).map((s) => [s.branchId, s]));
  return branches.map((branch) => {
    const summary = byId.get(branch.id);
    const pointOfSale = branchPvMap[branch.id] ?? (summary?.pointOfSale != null ? String(summary.pointOfSale) : '');
    const validationMatchesPv =
      summary?.pointOfSale != null && Number(pointOfSale) === summary.pointOfSale;
    return {
      branchId: branch.id,
      branchName: summary?.branchName ?? branch.name,
      pointOfSale,
      pvValidatedAt: validationMatchesPv ? summary?.pvValidatedAt ?? null : null,
      pvLastInvoiceNumber: validationMatchesPv ? summary?.pvLastInvoiceNumber ?? null : null,
      pvSetupError: validationMatchesPv ? summary?.pvSetupError ?? null : null,
    };
  });
}

const styles = StyleSheet.create({
  shell: { gap: spacing.md, alignSelf: 'stretch' },
  intro: { ...typography.caption, color: ticketColors.ink2, lineHeight: 18 },
  stack: { gap: 0 },
  branchRow: {
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: ticketColors.rule,
    paddingTop: spacing.md,
    paddingBottom: spacing.sm,
    gap: spacing.sm,
  },
  branchRowWide: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    paddingVertical: spacing.sm,
  },
  rowInfo: { gap: spacing.xs },
  rowInfoWide: { flex: 1, minWidth: 0, gap: spacing.xs },
  inputWide: { width: 120, flexShrink: 0 },
  pvFieldWide: { flexShrink: 0, gap: spacing.xs },
  validateAllWide: { alignSelf: 'flex-start', paddingHorizontal: spacing.xl },
  validateBtnWide: { minWidth: 180, flexShrink: 0 },
  currentRow: {
    borderLeftWidth: 3,
    borderLeftColor: ticketColors.gold,
    paddingLeft: spacing.sm,
  },
  wideRowHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.sm,
  },
  branchName: {
    ...typography.body,
    color: ticketColors.ink,
    fontWeight: '700',
    flex: 1,
  },
  label: {
    ...typography.caption,
    color: ticketColors.ink2,
    textTransform: 'uppercase',
    letterSpacing: 0.8,
  },
  input: {
    backgroundColor: ticketColors.paper,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    color: ticketColors.ink,
    minHeight: 44,
  },
  meta: { ...typography.caption, color: ticketColors.ink3, fontVariant: ['tabular-nums'] },
  errorText: { ...typography.caption, color: ticketColors.bad, lineHeight: 18 },
  validateBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.xs,
    paddingVertical: spacing.sm,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper2,
  },
  validateBtnText: { ...typography.caption, color: ticketColors.ink, fontWeight: '700' },
  disabled: { opacity: 0.5 },
});
